// Central translation dictionary for the storefront.
//
// Keys are the ENGLISH source string. The display language is one of:
//   'US' -> English  (returns the key as-is, no lookup needed)
//   'BA' -> Lokalni (srp/hrv/bos)
//   'RU' -> Русский
//
// Product/category *content* (names, descriptions) is NOT handled here — that
// comes from the API columns nameBA/nameUS/nameRU. This file is only for the
// fixed UI chrome (buttons, labels, nav, cart, checkout, etc.).
//
// Any component can read the active language / translate via the
// `useLanguage()` hook (no prop drilling needed) as long as it's rendered
// inside <LanguageProvider>.

import React, { createContext, useContext, useMemo } from 'react';

// Every language the dictionary knows about. What's actually offered to
// visitors is the narrower OFFERED_LANGUAGES in i18n/languageAccess.js.
export const LANGUAGES = [
  { code: 'BA', label: 'SR', name: 'Srpski' },
  { code: 'US', label: 'EN', name: 'English' },
  { code: 'RU', label: 'RU', name: 'Русский' },
];

export const translations = {
  "BA": {
    "Shop By Categories": "Kategorije",
    "Available Now": "Dostupno",
    "Ceramics": "Keramika",
    "Add to cart": "Dodaj u korpu",
    "Added to cart": "U korpi",
    "Order Now": "Naruči sada",
    "Loading...": "Učitavanje...",
    "Read more": "Pročitaj više",
    "Show less": "Prikaži manje",
    "Home": "Početna",
    "Contact": "Kontakt",
    "About": "O nama",
    "Shop": "Shop",
    "Paintings": "Slike",
    "Search products...": "Pretraži proizvode...",
    "Search": "Pretraga",
    "Cart": "Korpa",
    "Your cart is empty.": "Korpa je prazna.",
    "View Cart": "Pogledaj korpu",
    "My Cart": "Korpa",
    "Delivery": "Poštarina",
    "Subtotal": "Međuzbir",
    "Total": "Ukupno",
    "Qty:": "Količina:",
    "Quantity": "Količina",
    "Cart is empty.": "Korpa je prazna.",
    "Checkout": "Kompletiraj narudžbu",
    "Edit Cart": "Uredi korpu",
    "Order": "Naruči",
    "Remove": "Ukloni",
    "Continue shopping": "Nastavi kupovinu",
    "Your cart": "Vaša korpa",
    "Browse paintings": "Pregledaj slike",
    "Free": "Besplatno",
    "Continue to payment": "Nastavi na plaćanje",
    "Cash on delivery · Delivery across the region": "Plaćanje pouzećem · Dostava širom regiona",
    "Set": "Set",
    "Color": "Boja",
    "Set is currently out of stock": "Set trenutno nije na stanju",
    "Accessories": "Dodaci",
    "Description": "Opis",
    "Date": "Datum",
    "Reference images": "Slike za referencu",
    "Delivery within": "Dostava u roku od",
    "Due to handmade production and personalization, the mug takes about 4 weeks to make.": "Zbog ručne izrade i personalizacije, šoljica zahtijeva oko 4 sedmice za izradu.",
    "Due to handmade production and personalization, the plate takes about 4 weeks to make.": "Zbog ručne izrade i personalizacije, tanjir zahtijeva oko 4 sedmice za izradu.",
    "Product is currently out of stock": "Proizvod trenutno nije na stanju",
    "Notify me when available": "Obavijesti me kad bude dostupno",
    "Out of stock": "Nije na stanju",
    "In stock": "Na stanju",
    "Available": "Dostupna",
    "Currently unavailable": "Trenutno nedostupna",
    "SALE": "SNIŽENO",
    "Choose size": "Izaberi dimenziju",
    "Order Summary": "Pregled narudžbe",
    "Order summary": "Pregled narudžbe",
    "Thank You": "Hvala Vam",
    "Your order has beed received": "Vaša narudžba je primljena",
    "You will receive an email once your order is confirmed.": "Primit ćete e-mail kada vaša narudžba bude potvrđena.",
    "Products": "Proizvodi",
    "Information": "Informacije",
    "Delivery & Returns": "Dostava i povrat",
    "Privacy Policy": "Politika privatnosti",
    "Terms of Use": "Uslovi korišćenja",
    "Contact us": "Kontaktiraj nas",
    "All rights reserved.": "Sva prava zadržana.",
    "View all": "Pogledaj sve",
    "Workshops": "Radionice",
    "Ceramic angel": "Keramički anđeo",
    "Shipping is calculated at checkout": "Dostava se računa na blagajni",
    "This site uses cookies for better functionality.": "Ovaj sajt koristi kolačiće radi bolje funkcionalnosti.",
    "Learn more": "Saznaj više",
    "OK": "U redu",
    "Order details": "Detalji narudžbe",
    "Order ID": "Id narudžbe",
    "Payment method": "Način plaćanja",
    "Ordered products": "Naručeni proizvodi",
    "Name": "Ime",
    "Email": "Email",
    "Phone": "Telefon",
    "Country": "Država",
    "City": "Grad",
    "Address": "Adresa",
    "Text": "Tekst",
    "Bosnia and Herzegovina": "Bosna i Hercegovina",
    "Serbia": "Srbija",
    "Croatia": "Hrvatska",
    "Montenegro": "Crna Gora",
    "This field is required": "Ovo polje je obavezno",
    "Invalid email address": "Neispravna email adresa",
    "Invalid phone number": "Neispravan broj telefona",
    "Customer information": "Podaci kupca",
    "Confirmation email": "Email za potvrdu narudžbe",
    "First name": "Ime",
    "Last name": "Prezime",
    "Phone number": "Broj telefona",
    "Shipping information": "Podaci za dostavu",
    "Zip code": "Poštanski broj",
    "Cash on delivery": "Plaćanje pouzećem",
    "Card payment": "Plaćanje karticom",
    "European Union": "Evropska unija",
    "Shipping to this country isn't available yet. Please contact us on Instagram.": "Dostava u ovu zemlju još nije dostupna. Kontaktirajte nas na Instagramu.",
    "Enter a valid Bosnian phone number": "Unesite ispravan broj telefona iz BiH",
    "Enter a valid Serbian phone number": "Unesite ispravan broj telefona iz Srbije",
    "Enter a valid Montenegrin phone number": "Unesite ispravan broj telefona iz Crne Gore",
    "Enter a valid Croatian phone number": "Unesite ispravan broj telefona iz Hrvatske",
    "Shipping method": "Način dostave",
    "Payment": "Plaćanje",
    "Show order summary": "Prikaži pregled narudžbe",
    "Hide order summary": "Sakrij pregled narudžbe",
    "Pay in cash when the courier delivers your order.": "Plaćate gotovinom kuriru prilikom preuzimanja paketa.",
    "You will enter your card details securely on the next step.": "Podatke o kartici unosite sigurno u sljedećem koraku.",
    "All transactions are secure and encrypted.": "Sva plaćanja su sigurna i šifrovana.",
    "Name on card": "Ime na kartici",
    "Use shipping address as billing address": "Koristi adresu dostave kao adresu za naplatu",
    "Courier delivery to your address": "Dostava kurirskom službom na adresu",
    "Pay": "Plati",
    "Amount due": "Iznos za plaćanje",
    "Checking payment...": "Provjeravamo plaćanje...",
    "Payment failed. Please try again.": "Plaćanje nije uspjelo. Pokušajte ponovo.",
    "Payment is being processed. You will receive a confirmation email once it is completed.": "Plaćanje je u obradi. Dobićete email potvrdu čim bude završeno.",
    "Card payment is unavailable right now. Please try again later or contact us on Instagram.": "Plaćanje karticom trenutno nije dostupno. Pokušajte ponovo kasnije ili nas kontaktirajte na Instagramu.",
    "This payment was canceled. Please contact us on Instagram.": "Ovo plaćanje je otkazano. Kontaktirajte nas na Instagramu.",
    "Your order is saved and will be confirmed once the payment is completed.": "Vaša narudžba je sačuvana i biće potvrđena čim plaćanje bude završeno.",
    "Processing...": "Obrada...",
    "Limited stock": "Zalihe su ograničene",
    "Additional information": "Dodatne informacije",
    "Gold text inside the mug (optional)": "Zlatni tekst unutar šolje (opcionalno)",
    "Describe the details you want on the platter:": "Opis detalja koje želite na pladnju:",
    "e.g. I love you ❤️": "Npr. Volim te ❤️",
    "Example: We have a dog, she loves walks, could be a cake and balloons, or a cup of tea": "Na primer: Imamo psa, ona voli šetnje, može da bude kolač i baloni, može tipa šoljica čaja",
    "Date you need the product": "Datum kada vam je potreban proizvod",
    "Choose a date": "Izaberite datum",
    "Earliest available date": "Najraniji dostupan datum",
    "The product requires at least 1 month to make. Please choose a date at least 1 month from today": "Proizvod zahtjeva najmanje 1 mjesec za izradu. Molimo izaberite datum najmanje 1 mjesec od danas",
    "or later": "ili kasnije",
    "Choose a color": "Izaberite boju",
    "Choose a bead color": "Izaberite boju perlica",
    "You can choose more than one color.": "Možete izabrati više boja.",
    "Selected color": "Odabrana boja",
    "Your first accessory is free.": "Prvi dodatak je besplatan.",
    "Green": "Zelena",
    "Pink": "Roza",
    "Blue": "Plava",
    "White/beige": "Bijela/bež",
    "Black": "Crna",
    "Add accessories (optional)": "Dodaj Dodatke (Opcionalno)",
    "Want something else? Describe your own accessory": "Želite nešto drugo? Opišite svoj dodatak",
    "each": "po komadu",
    "e.g. a small crown, a bow tie, a fish…": "npr. mala kruna, leptir mašna, ribica…",
    "Add your own accessory": "Dodaj svoj dodatak",
    "custom accessories": "custom dodataka",
    "Custom accessories": "Custom dodaci",
    "How many pets on the mug?": "Koliko ljubimaca na šoljici?",
    "How many pets on the pendant?": "Koliko ljubimaca na privjesku?",
    "How many pets on the plate?": "Koliko ljubimaca na tanjiru?",
    "How many pets on the jewelry dish?": "Koliko ljubimaca na tanjiriću?",
    "Each additional pet": "Svaki sljedeći ljubimac",
    "Reference images of all pets": "Slike za referencu svih ljubimaca",
    "1 pet": "1 ljubimac",
    "2 pets": "2 ljubimca",
    "Included in price": "Uključeno u cijenu",
    "Number of pets": "Broj ljubimaca",
    "Add a reference photo of both pets so we can paint them together.": "Dodajte referentnu fotografiju oba ljubimca kako bismo ih naslikali zajedno.",
    "for": "za",
    "accessory": "dodatak",
    "accessories (count)": "dodatka",
    "optional": "opcionalno",
    "The following files are not allowed (only images are allowed):": "Sledeće datoteke nisu dozvoljene (samo slike su dozvoljene):",
    "Error processing images. Please try again.": "Greška pri obradi slika. Molimo pokušajte ponovo.",
    "Error processing images.": "Greška pri obradi slika.",
    "You must add at least 1 image.": "Obavezno dodajte barem 1 sliku.",
    "Add at least one clear photo of each pet.": "Dodajte bar jednu jasnu fotografiju svakog ljubimca.",
    "Reference images of both pets": "Slike za referencu sa oba ljubimca",
    "You can add up to": "Možete dodati do",
    "images": "slike",
    "image": "sliku",
    "Allowed formats: JPEG, PNG, GIF, WEBP.": "Dozvoljeni formati: JPEG, PNG, GIF, WEBP.",
    "You've added the maximum number of images (3/3).": "Dodali ste maksimalan broj slika (3/3).",
    "You've added the maximum number of images.": "Dodali ste maksimalan broj slika.",
    "Choose a set": "Odaberite set",
    "Sold out": "Rasprodato",
    "Low stock": "Malo na stanju",
    "items left": "komada preostalo",
    "Please fill in all required fields": "Molimo popunite sva obavezna polja",
    "Please enter text": "Molimo unesite tekst",
    "Please add an image": "Molimo dodajte sliku",
    "Added ✓": "Dodano ✓",
    "Product information": "Informacije o proizvodu",
    "Delivery information": "Informacije o dostavi",
    "Over 100+ happy pets already have their pendant.": "Preko 100+ zadovoljnih ljubimaca već ima svoj privjesak ❤️",
    "Over 100+ happy pets already have their mug.": "Preko 100+ zadovoljnih ljubimaca već ima svoju šoljicu ❤️",
    "Bulka Ceramics customer pet": "Ljubimac naše mušterije na Bulka Ceramics privjesku",
    "Packaging": "Pakovanje",
    "The box our little plates and mugs arrive in.": "Kutija u kojoj stižu naši tanjirići i šoljice.",
    "customer comment": "komentar kupca",
    "You may also like": "Možda Vam se svidi",
    "has arrived!": "je stigla!",
    "is coming soon!": "uskoro stiže!",
    "View the summer collection": "Pogledaj ljetnu kolekciju",
    "View the entire summer collection": "Pogledaj cijelu ljetnu kolekciju",
    "Coming soon": "Uskoro",
    "New collection coming soon!": "Nova kolekcija uskoro stiže!",
    "Days": "Dana",
    "Hours": "Sati",
    "Minutes": "Minuta",
    "Seconds": "Sekundi",
    "View the entire collection": "Pogledaj cijelu kolekciju",
    "View all partnership products": "Pogledaj sve partnership proizvode",
    "Ceramics workshops": "Radionice keramike",
    "Reserve a spot": "Rezerviši mjesto",
    "2-3 hours": "2-3 sata",
    "8-12 participants": "8-12 učesnika",
    "Our Story": "Naša Priča",
    "Who is Bulka?": "Ko je Bulka?",
    "Select country": "Izaberite zemlju",
    "Category not found": "Kategorija nije pronađena",
    "Category \"{name}\" does not exist.": "Kategorija \"{name}\" ne postoji.",
    "Back to home": "Vrati se na početnu",
    "Showing {count} products": "Prikazano {count} proizvoda",
    "Sort by:": "Sortiraj po:",
    "Featured": "Izdvojeno",
    "Price, low to high": "Cijena, niska → visoka",
    "Price, high to low": "Cijena, visoka → niska",
    "Date, oldest to newest": "Datum, najstariji → najnoviji",
    "Date, newest to oldest": "Datum, najnoviji → najstariji",
    "No products in this category": "Nema proizvoda u ovoj kategoriji",
    "No products currently available to display.": "Trenutno nema dostupnih proizvoda za prikaz.",
    "Collection not found": "Kolekcija nije pronađena",
    "Collection \"{name}\" does not exist.": "Kolekcija \"{name}\" ne postoji.",
    "Loading collection...": "Učitavanje kolekcije...",
    "No products in this collection": "Nema proizvoda u ovoj kolekciji",
    "Limited edition. Available until {date} or while supplies last.": "Limitirano izdanje. Dostupno do {date} ili do posljednjeg komada.",
    "No products": "Nema proizvoda",
    "Exclusive collection of ceramic products created through the Bulkaceramics and AteljeStojanović collaboration.": "Ekskluzivna kolekcija keramičkih proizvoda nastala kroz saradnju Bulkaceramics i AteljeStojanović.",
    "Each piece is handmade with special attention to detail.": "Svaki komad ručno je izrađen s posebnom pažnjom na detalje.",
    "Join our interactive ceramics workshops and discover the magic of creating with your own hands. You will learn the basic techniques of working with clay, shaping, and drawing. The workshops are suitable for all ages and experience levels — from beginners to those who want to perfect their skills.": "Pridružite se našim interaktivnim radionicama keramike i otkrijte čaroliju stvaranja vlastitim rukama. Naučićete osnovne tehnike rada s glinom, oblikovanje i crtanje. Radionice su prilagođene svim uzrastima i nivoima iskustva - od početnika do onih koji žele usavršiti svoje veštine.",
    "Each participant will have the opportunity to create their own unique ceramic piece to take home as a keepsake after firing.": "Svaki učesnik će imati priliku da stvori svoj jedinstven keramički komad koji će poneti kući kao uspomenu nakon pečenja.",
    "This is the kind of box you treasure just as much as what's inside. 😍 Pure magic! ✨ I would never have the heart to throw it away. 🙈🥰": "Ovo je ona kutija koju čuvaš podjednako kao i ono što se nalazi unutra. 😍 Čista magija! ✨ Ne bih imala srca ikada da je bacim. 🙈🥰",
    "Bulka Ceramics is a family brand born from a love of creating unique ceramics. After years of perfecting our craft, we overcame challenges in the firing process and created collections that tell a story.": "Bulka Ceramics je porodični brend nastao iz ljubavi prema stvaranju unikatne keramike. Nakon godina usavršavanja zanata, prebrodili smo izazove u procesu pečenja i kreirali kolekcije koje pričaju priču.",
    "Bulka is the nickname of our beloved bull terrier Bea, who is the heart and soul of our brand. Her energy, persistence, and unique character inspire us in creating every ceramic piece.": "Bulka je nadimak za našeg omiljenog bullterijera Beu, koja je srce i duša našeg brenda. Njena energija, upornost i jedinstven karakter inspirišu nas u stvaranju svakog keramičkog komada.",
    "New Year Collection": "Novogodišnja Kolekcija",
    "New Year collection is coming soon": "Novogodišnja kolekcija uskoro dolazi",
    "Handmade ceramic ornaments and gifts for the holidays, made in small batches.": "Ručno rađeni keramički ukrasi i pokloni za praznike, pravljeni u malim serijama.",
    "Leave your email and we'll let you know as soon as it's out.": "Ostavite email i javićemo vam čim kolekcija izađe.",
    "Your email": "Vaš email",
    "Thank you! We'll let you know as soon as the collection is out.": "Hvala! Javićemo vam čim kolekcija izađe.",
    "Something went wrong. Please try again.": "Nešto nije u redu. Pokušajte ponovo.",
    "No products in the New Year collection": "Nema proizvoda u novogodišnjoj kolekciji",
    "Invalid email format": "Neispravan format email adrese",
    "Your submission has been received. We will be in touch and contact you soon!": "Vaša prijava je zaprimljena. Javit ćemo vam se i kontaktirati vas uskoro!",
    "Error!": "Greška!",
    "We couldn't complete your request. Please contact us via email at contact@bulkaceramics.com": "Nismo mogli izvršiti vaš zahtjev. Molimo kontaktirajte nas putem emaila na contact@bulkaceramics.com",
    "First Name": "Ime",
    "Last Name": "Prezime",
    "Email*": "Email*",
    "Subject": "Predmet",
    "Message": "Poruka",
    "Submit": "Pošalji",
    "Shipping": "Dostava",
    "For Serbia:": "Za Srbiju:",
    "For Bosnia and Herzegovina:": "Za Bosnu i Hercegovinu:",
    "For Montenegro:": "Za Crnu Goru:",
    "For Croatia:": "Za Hrvatsku:",
    "Delivery within 30 days.": "Isporuka se vrši u roku od 30 dana.",
    "Delivery within 20 days.": "Isporuka se vrši u roku od 20 dana.",
    "By arrangement.": "Isporuka se vrši po dogovoru.",
    "If your product is damaged during delivery, please send us photos of the damaged item within 3 business days. In that case, we can send a new product or issue a refund.": "Ukoliko je proizvod oštećen tokom isporuke, molimo vas da nam dostavite slike oštećenog artikla u roku od 3 radna dana. U tom slučaju, možemo poslati novi proizvod ili izvršiti povrat novca.",
    "If we have the product available, we can replace it immediately. If the product is not available, we will issue a refund.": "Ukoliko imamo dostupan proizvod, možemo ga odmah zameniti. Ako proizvod nije dostupan, izvršićemo povraćaj novca.",
    "If you would like to return a product, please contact us at:": "Ukoliko želite izvršiti povrat proizvoda, molimo vas da nas kontaktirate na email:",
    "Our team will help you with all the necessary information and the return process.": "Naš tim će vam pomoći sa svim potrebnim informacijama i postupkom povrata.",
    "For international delivery, feel free to contact us at the same email.": "Za međunarodnu isporuku, slobodno nas kontaktirajte na isti email.",
    "Reservation successfully sent!": "Rezervacija je uspešno poslana!",
    "Workshop:": "Radionica:",
    "Time:": "Vreme:",
    "Error: {message}": "Greška: {message}",
    "An error occurred while sending the reservation. Please try again.": "Došlo je do greške prilikom slanja rezervacije. Molimo pokušajte ponovo.",
    "An error occurred while signing up for notifications. Please try again.": "Došlo je do greške prilikom prijave za obaveštenja. Molimo pokušajte ponovo.",
    "Ceramic Workshops": "Radionice keramike",
    "Discover the magic of creating with your own hands. Our workshops are suited to all ages and experience levels - from beginners to those looking to refine their skills.": "Otkrijte čaroliju stvaranja vlastitim rukama. Naše radionice su prilagođene svim uzrastima i nivoima iskustva - od početnika do onih koji žele usavršiti svoje veštine.",
    "Gallery from our workshops": "Galerija sa naših radionica",
    "Locations and schedule": "Lokacije i termini",
    "Loading workshops...": "Učitavanje radionica...",
    "No workshops currently available": "Trenutno nema dostupnih radionica",
    "Fully booked": "Popunjeno",
    "Open": "Dostupno",
    "per person": "po osobi",
    "Available times:": "Termini:",
    "1 spot available": "1 mjesto dostupno",
    "{n} spots available": "{n} mjesta dostupna",
    "Reserve": "Rezerviši",
    "What can you expect?": "Šta možete očekivati?",
    "Learning basic techniques": "Učenje osnovnih tehnika",
    "Shaping, glazing and basic clay working techniques": "Oblikovanje, glaziranje i osnovne tehnike rada sa glinom",
    "Creating your own piece": "Stvaranje vlastitog dela",
    "Each participant creates their own unique ceramic piece": "Svaki učesnik kreira svoj jedinstveni keramički komad",
    "Limited number of spots": "Ograničen broj mjesta",
    "The number of spots is limited to ensure dedicated attention to each participant": "Broj mjesta je ograničen radi posvećenosti svakom učesniku",
    "Location: {name}": "Lokacija: {name}",
    "Email address": "Email adresa",
    "Full name": "Ime i prezime",
    "Your name": "Vaše ime",
    "Cancel": "Otkaži",
    "Sign me up": "Prijavi me",
    "Workshop reservation": "Rezervacija radionice",
    "First name *": "Ime *",
    "Last name *": "Prezime *",
    "Your last name": "Vaše prezime",
    "Email address *": "Email adresa *",
    "Phone number *": "Broj telefona *",
    "Note (optional)": "Napomena (opciono)",
    "Special requests or questions...": "Posebni zahtevi ili pitanja...",
    "Confirm reservation": "Potvrdi rezervaciju",
    "Notification": "Obaveštenje",
    "Email is required": "Email je obavezan",
    "Please add at least one image": "Dodajte barem jednu sliku",
    "Thank you!": "Hvala Vam!",
    "Personalized Illustrations": "Personalne Ilustracije",
    "Custom illustrations": "Personalizirane ilustracije",
    "Turn your favorite photo into a unique illustration made to your wishes. Illustrations are printed as high-quality giclée prints in formats from A5 to A2.": "Pretvorite svoju omiljenu fotografiju u jedinstvenu ilustraciju izrađenu prema vašim željama. Ilustracije se printaju kao visokokvalitetni giclée printovi u formatima od A5 do A2.",
    "Price list:": "Cjenik:",
    "For one person:": "Za jednu osobu:",
    "Up to A4:": "Do A4:",
    "For two or three people:": "Za dvije ili tri osobe:",
    "Up to A3:": "Do A3:",
    "Additional people:": "Dodatne osobe:",
    "Pets:": "Ljubimci:",
    "Included at no extra charge": "Uključeni bez dodatne naknade",
    "Treat yourself or a loved one to a memory that will brighten up any space!": "Poklonite sebi ili dragoj osobi uspomenu koja će uljepšati svaki prostor!",
    "How to send photos for illustration?": "Kako poslati fotografije za ilustraciju?",
    "Attach clear photos": "Priložite jasne fotografije",
    "that you want used to create the illustration.": "koje želite da se koriste za izradu ilustracije.",
    "We recommend high-quality photos, with good lighting and visible facial and clothing details.": "Preporučujemo fotografije visoke kvalitete, s dobrim osvjetljenjem i vidljivim detaljima lica i odjeće.",
    "You can attach multiple photos if you want to combine different details in one illustration.": "Možete priložiti više fotografija ako želite kombinirati različite detalje u jednoj ilustraciji.",
    "If you have special requests (backgrounds, colors, clothing style or additional elements), write them in the description.": "Ako imate posebne želje (pozadine, boje, stil odjeće ili dodatni elementi), napišite ih u opisu.",
    "Allowed photo formats:": "Dozvoljeni formati fotografija:",
    "Click on": "Kliknite na",
    "Add images": "Dodaj slike",
    "to attach your photos, then fill in the other details and submit your request.": "kako biste priložili svoje fotografije, a zatim ispunite ostale podatke i pošaljite zahtjev.",
    "Field is required": "Polje je obavezno",
    "Proposal received": "Prijedlog primljen",
    "Thank you for your interest. We carefully consider every proposal and will get back to you if we believe there's a good fit.": "Hvala vam na interesu. Pažljivo razmatramo svaki prijedlog i javit ćemo vam se ukoliko smatramo da postoji dobro uklapanje.",
    "Error": "Greška",
    "We couldn't receive your proposal. Please contact us directly at contact@bulkaceramics.com": "Nismo mogli primiti vaš prijedlog. Molimo kontaktirajte nas direktno na contact@bulkaceramics.com",
    "Find us": "Pronađite nas",
    "Store map": "Mapa prodavnica",
    "Our products are available at selected locations.": "Naši proizvodi dostupni su na odabranim lokacijama.",
    "Shown on the map": "Prikazano na mapi",
    "More locations coming soon": "Više lokacija uskoro",
    "Partnership": "Partnerstvo",
    "Your store": "Vaša prodavnica",
    "on this map?": "na ovoj mapi?",
    "Our products can currently be found at selected locations. If you run a store and think our products would be a good fit — we'd love to hear from you. We choose partners carefully.": "Naši proizvodi trenutno se mogu naći na odabranim lokacijama. Ako vodite prodavnicu i smatrate da bi naši proizvodi bili dobro uklapanje — volimo čuti od vas. Biramo partnere pažljivo.",
    "Selective approach": "Selektivan pristup",
    "Each partner goes through an individual selection process.": "Svaki partner prolazi individualni odabir.",
    "Handmade, high value": "Ručna izrada, visoka vrijednost",
    "Products that sell themselves.": "Proizvodi koji se prodaju sami sobom.",
    "Growing brand": "Rastući brend",
    "An active online community that directs customers to stores.": "Aktivna online zajednica koja vas upućuje u prodavnice.",
    "Store name": "Naziv prodavnice",
    "e.g. My Store": "npr. Moja Prodavnica",
    "Contact email": "Kontakt email",
    "Why do you think we'd be a good fit?": "Zašto mislite da smo dobro uklapanje?",
    "Tell us about your store, customers, and why you think our products would be a good choice...": "Recite nam nešto o vašoj prodavnici, kupcima i zašto smatrate da bi naši proizvodi bili dobar izbor...",
    "Sending…": "Slanje…",
    "Send us your proposal": "Pošaljite nam prijedlog",
    "Data Collection": "Prikupljanje podataka",
    "Data Usage": "Korišćenje podataka",
    "Data Protection": "Zaštita podataka",
    "Cookies and Tracking Technologies": "Kolačići i tehnologije praćenja",
    "Changes to the Privacy Policy": "Promene politike privatnosti",
    "General Terms": "Opšti uslovi",
    "Orders": "Narudžbe",
    "Limitation of Liability": "Ograničenja odgovornosti",
    "Your privacy is very important to us. This privacy policy explains how we collect, use, and protect your personal data.": "Vaša privatnost je za nas veoma važna. Ova politika privatnosti objašnjava kako prikupljamo, koristimo i štitimo vaše lične podatke.",
    "We collect information you voluntarily provide when registering on our website or when ordering products. This information includes, but is not limited to: name, address, email address, and phone number.": "Prikupljamo informacije koje nam dobrovoljno dostavite prilikom registracije na našoj web stranici ili prilikom naručivanja proizvoda. Ove informacije uključuju, ali nisu ograničene na: ime, adresu, email adresu i broj telefona.",
    "We use your data to provide you with quality service, process your orders, and provide you with information about our products and services.": "Vaše podatke koristimo kako bismo vam omogućili kvalitetnu uslugu, obradili vaše narudžbe i pružili vam informacije o našim proizvodima i uslugama.",
    "We take appropriate technical and organizational measures to protect your personal data from unauthorized access, loss, or destruction.": "Preduzimamo odgovarajuće tehničke i organizacione mere kako bismo zaštitili vaše lične podatke od neovlašćenog pristupa, gubitka ili uništenja.",
    "Our website uses cookies and similar tracking technologies to improve your user experience.": "Naša web stranica koristi kolačiće i slične tehnologije praćenja kako bismo poboljšali vaše korisničko iskustvo.",
    "You can set your cookie preferences in your browser settings. Please note that disabling cookies may affect your ability to use certain features of our website.": "Možete postaviti svoje postavke kolačića u podešavanjima vašeg pregledača. Imajte na umu da isključivanje kolačića može uticati na vašu mogućnost korišćenja određenih funkcija naše web stranice.",
    "We reserve the right to change this privacy policy at any time. All changes will be published on this page, and the date of the last modification will be updated. We recommend that you check this page periodically to stay informed of any changes.": "Zadržavamo pravo da u bilo kojem trenutku izmenimo ovu politiku privatnosti. Sve promene će biti objavljene na ovoj stranici, a datum poslednje izmene će biti ažuriran. Preporučujemo vam da povremeno proveravate ovu stranicu kako biste bili informisani o svim promenama.",
    "At any time, you can request a change to your personal data. For any inquiries, contact us at email:": "U bilo kojem trenutku možete zatražiti izmenu vaših ličnih podataka. Za bilo kakve upite, kontaktirajte nas na email:",
    "By using this site, you agree to the following terms of use:": "Korišćenjem ove stranice, pristajete na sledeće uslove korišćenja:",
    "All content on this site, including text, images, and graphics, is protected by copyright. Use of any part of the content without our permission is not allowed.": "Svi sadržaji na ovoj stranici, uključujući tekst, slike i grafiku, zaštićeni su autorskim pravima. Korišćenje bilo kojeg dela sadržaja bez naše dozvole nije dozvoljeno.",
    "When ordering products, the user undertakes to provide accurate and complete information. If an error occurs in the order, we reserve the right to cancel it.": "Prilikom naručivanja proizvoda, korisnik se obavezuje da će pružiti tačne i potpune informacije. Ukoliko dođe do greške u narudžbi, zadržavamo pravo da je otkazujemo.",
    "We are not responsible for any damages or losses that may result from the use of our products or services. We recommend that you carefully read all information before making a purchase.": "Nismo odgovorni za bilo kakve štete ili gubitke koji mogu nastati kao rezultat korišćenja naših proizvoda ili usluga. Preporučujemo vam da pažljivo pročitate sve informacije pre nego što izvršite kupovinu.",
    "For any additional information or questions regarding our terms of use, feel free to contact us at email:": "Za sve dodatne informacije ili pitanja u vezi sa našim uslovima korišćenja, slobodno nas kontaktirajte na email:",
    "Page Not Found": "Stranica nije pronađena",
    "Go to home": "Idi na početnu",
    "Follow on Instagram": "Prati na Instagramu",
    "Be the first to know": "Budite prvi koji saznaju",
    "when a new painting is": "kada je nova slika",
    "available.": "dostupna.",
    "Subscribed": "Prijavljeni ste",
    "Sending...": "Slanje...",
    "Subscribe me": "Prijavite me",
    "Your data is safe. We will never share it.": "Vaši podaci su sigurni. Nikada ih nećemo dijeliti.",
    "We'll be in touch as soon as a new painting is available.": "Javićemo se čim bude nova slika dostupna.",
    "Close": "Zatvori",
    "What clients say": "Što klijenti kažu",
    "Paintings that": "Slike koje",
    "leave a mark.": "ostavljaju trag.",
    "Instagram message": "Instagram poruka",
    "Available motifs": "Dostupni motivi",
    "Choose your": "Odaberite svoju",
    "painting": "sliku",
    "Available paintings can be ordered right away. Paintings that are currently unavailable can be ordered via Instagram or email.": "Dostupne slike možete odmah naručiti. Slike koje trenutno nisu dostupne možete naručiti putem Instagrama ili emaila.",
    "Paintings that will fill": "Slike koje će ispuniti",
    "your home with": "vaš dom",
    "love.": "ljubavlju.",
    "View available paintings": "Pogledaj dostupne slike",
    "Each painting is created with special": "Svaka slika nastaje sa posebnom",
    "care and love,": "pažnjom i ljubavlju,",
    "created to stay in your home forever.": "stvorena da ostane u vašem domu zauvijek.",
    "Paintings that will fill your home with love.": "Slike koje će ispuniti vaš dom ljubavlju.",
    "Required field": "Obavezno polje",
    "Invalid email": "Nevažeći email",
    "Back": "Nazad",
    "Customer details": "Podaci o kupcu",
    "Shipping address": "Adresa dostave",
    "ZIP code": "Poštanski broj",
    "An error occurred while submitting your order. Please try again or contact us on Instagram.": "Došlo je do greške pri slanju narudžbe. Pokušajte ponovo ili nas kontaktirajte na Instagramu.",
    "Estimated delivery": "Procijenjeni rok dostave",
    "Due to handmade craftsmanship and personalization, the product requires about 4 weeks to make.": "Zbog ručne izrade i personalizacije, proizvod zahtijeva oko 4 sedmice za izradu.",
    "Thank you for your order!": "Hvala na narudžbi!",
    "Dear {name}, your order has been received.": "Draga/i {name}, vaša narudžba je primljena.",
    "Your order has been received.": "Vaša narudžba je primljena.",
    "We'll be in touch as soon as the painting has been shipped.": "Javit ćemo vam se čim slika bude poslana.",
    "Confirmation sent to": "Potvrda je poslana na",
    "Order number": "Broj narudžbe",
    "Ordered paintings": "Naručene slike",
    "Painting": "Slika",
    "Painting not found.": "Slika nije pronađena.",
    "Notify me": "Obavijesti me",
    "When it becomes available": "Kada bude dostupno",
    "Enter your email address and we will notify you when the painting becomes available again.": "Unesite email adresu i obavijestićemo vas kada slika bude dostupna ponovo.",
    "You will be notified when the painting becomes available.": "Bićete obaviješteni kada slika postane dostupna.",
    "Send": "Pošalji",
    "Enter your email address to be notified when the product becomes available again.": "Unesite vašu email adresu kako biste bili obaviješteni kada proizvod bude ponovo dostupan.",
    "You will be notified when the product becomes available.": "Bićete obaviješteni kada proizvod postane dostupan.",
    "Enter your email address": "Unesite vašu email adresu",
    "An error occurred while sending your data. Please try again.": "Greška prilikom slanja podataka. Molimo pokušajte ponovo.",
    "Your cart has reached its maximum size. Please try again with smaller images.": "Korpa je dostigla maksimalnu veličinu. Molimo pokušajte sa manjim slikama",
    "Please enter a valid email address": "Molimo unesite ispravnu email adresu",
    "Error. Please try again.": "Greška. Molimo pokušajte ponovo.",
    "Delivery date": "Dostava",
    "*The painting comes on a wooden blind frame. A decorative frame can be added later.": "*Slika dolazi na drvenom blind ram. Ukrasni ram se može staviti naknadno.",
    "Personalization": "Personalizacija",
    "Gold text inside the cup (optional)": "Zlatni tekst unutar šolje (opcionalno)",
    "Description of details you want on the tray": "Opis detalja koje želite na pladnju",
    "E.g. I love you ❤️": "Npr. Volim te ❤️",
    "For example: We have a dog, she loves walks, it can be a cake and balloons...": "Na primjer: Imamo psa, ona voli šetnje, može da bude kolač i baloni...",
    "Date you need the product by": "Datum kada vam je potreban proizvod",
    "Earliest date": "Najraniji datum",
    "The product requires at least 1 month to make ({date} or later).": "Proizvod zahtijeva najmanje 1 mjesec za izradu ({date} ili kasnije).",
    "Allowed formats: JPEG, PNG, GIF, WEBP": "Dozvoljeni formati: JPEG, PNG, GIF, WEBP",
    "You have added the maximum number of images (3/3).": "Dodali ste maksimalan broj slika (3/3).",
    "You must add at least 1 image. ": "Obavezno dodajte barem 1 sliku. ",
    "You can add up to 3 images.": "Možete dodati do 3 slike.",
    "Options": "Opcije",
    "day": "dan",
    "days": "dana",
    "Delivery time": "Dostava",
    "added to production time": "na rok izrade",
    "⚠ Limited stock — only {n} left": "⚠ Ograničene zalihe — samo {n} preostalo",
    "Not available": "Nije dostupno",
    "Estimated delivery time is 1–2 months from order.": "Okvirno vrijeme dostave je 1–2 mjeseca od narudžbe.",
    "The delivery cost will be included at checkout.": "Cijena dostave biće uračunata na checkoutu.",
    "Each product is handmade and carries its own unique character.": "Svaki proizvod izrađen je ručno i nosi svoj jedinstveni karakter.",
    "All materials are lead-free and food safe.": "Svi materijali su bez olova i sigurni za upotrebu s hranom.",
    "Each product is handmade and carries its own unique character. Due to handmade production, minor variations in shape, size, color, and pattern are possible, which makes each piece special and one of a kind.": "Svaki proizvod izrađen je ručno i nosi svoj jedinstveni karakter. Zbog ručne izrade moguća su manja odstupanja u obliku, veličini, boji i šari, što svaki komad čini posebnim i neponovljivim.",
    "Made of porcelain, painted with glaze colors and covered with a transparent glaze with 24K gold details.": "Proizvedeno od porcelana, oslikano glazurnim bojama i prekriveno prozirnom glazurom sa 24K zlatnim detaljima.",
    "All materials used are lead-free, non-toxic, and completely safe for use with food.": "Svi korišteni materijali su bez olova, netoksični i potpuno sigurni za upotrebu s hranom.",
    "To preserve the gold details and lasting shine, gentle hand washing is recommended.": "Za očuvanje zlatnih detalja i trajnog sjaja, savjetuje se nježno ručno pranje.",
    "The estimated delivery date is shown at checkout.": "Procijenjeni datum dostave prikazan je na checkoutu.",
    "Delivery is free for orders over 150 KM / 75 EUR / 9000 RSD.": "Dostava je besplatna za narudžbe preko 150 KM / 75 EUR / 9000 RSD.",
    "Free shipping on all orders over 9000 RSD within Serbia": "Besplatna dostava za sve porudžbine preko 9000 RSD na teritoriji Srbije",
    "Free shipping on all orders over 150 KM within Bosnia and Herzegovina": "Besplatna dostava za sve porudžbine preko 150 KM na teritoriji Bosne i Hercegovine",
    "Free shipping on all orders over 80 EUR within Croatia": "Besplatna dostava za sve porudžbine preko 80 EUR na teritoriji Hrvatske",
    "Free shipping on all orders over 80 EUR within Montenegro": "Besplatna dostava za sve porudžbine preko 80 EUR na teritoriji Crne Gore",
    "We recommend": "Preporučujemo",
    "You might also like": "Možda vam se svidi",
    "Up to {n} images.": "Do {n} slika.",
    "January": "Januar",
    "February": "Februar",
    "March": "Mart",
    "April": "April",
    "May": "Maj",
    "June": "Jun",
    "July": "Jul",
    "August": "Avgust",
    "September": "Septembar",
    "October": "Oktobar",
    "November": "Novembar",
    "December": "Decembar",
    "Sun": "Ne",
    "Mon": "Po",
    "Tue": "Ut",
    "Wed": "Sr",
    "Thu": "Če",
    "Fri": "Pe",
    "Sat": "Su",

    // --- Korporativni pokloni landing page ---
    "Business Gifts": "Poslovni pokloni",
    "Corporate gifts": "Korporativni pokloni",
    "Gifts nobody leaves in a drawer": "Pokloni koje niko ne ostavlja u fioci",
    "Mugs with your company's 3D logo, plates and ornaments from handmade porcelain. We make every piece in our atelier — gifts that stay on the desk, not in the closet.": "Šoljice sa 3D logom vaše firme, tanjiri i ukrasi od ručno rađenog porcelana. Svaki komad izrađujemo u našem ateljeu — za poklone koji ostaju na stolu, a ne u ormaru.",
    "Build your inquiry in 60 seconds": "Sastavite upit za 60 sekundi",
    "or write to us directly at": "ili nam pišite direktno na",
    "No obligation · Offer within 48h · For companies and weddings": "Bez obaveze · Ponuda u roku od 48h · Za firme i vjenčanja",
    "Request an offer": "Zatražite ponudu",
    "Why porcelain, and not another notebook?": "Zašto porcelan, a ne još jedan rokovnik?",
    "A gift used every day": "Poklon koji se koristi svaki dan",
    "A mug with a logo sits on a desk for years. Notebooks run out, pens get lost — porcelain stays.": "Šoljica sa logom stoji na radnom stolu godinama. Rokovnik se potroši, hemijska se izgubi — porcelan ostaje.",
    "A 3D logo, not print": "3D logo, ne štampa",
    "We don't print your logo — we model it in relief, as part of the mug itself. You can see and feel the difference.": "Logo vaše firme ne štampamo — modelujemo ga u reljefu, kao dio same šoljice. Razlika se vidi i osjeti pod prstima.",
    "Handmade, small batches": "Ručna izrada, male serije",
    "Every piece is cast, sanded and painted by hand. No two gifts are exactly the same — just like the people receiving them.": "Svaki komad izlivamo, brusimo i oslikavamo ručno. Nijedna dva poklona nisu potpuno ista — baš kao ni ljudi kojima ih poklanjate.",
    "Packaging you don't throw away": "Pakovanje koje se ne baca",
    "Every gift arrives in packaging we care about as much as the piece itself — ready to hand over, no extra wrapping needed.": "Svaki poklon stiže u pakovanju o kojem vodimo računa koliko i o samom proizvodu — spremno za uručenje, bez dodatnog umotavanja.",
    "What we can make for you": "Šta možemo izraditi za vas",
    "These are the most common options — but if you have your own idea, tell us. We love unusual requests.": "Ovo su najčešće opcije — ali ako imate svoju ideju, recite nam. Volimo neobične zahtjeve.",
    "Mugs with a 3D logo": "Šoljice sa 3D logom",
    "Your company's logo in relief, colors of your choice. The most requested business gift from our workshop.": "Logo vaše firme u reljefu, boje po želji. Najtraženiji poslovni poklon iz naše radionice.",
    "Mugs and plates with names": "Šoljice i tanjiri sa imenima",
    "A personalized piece for every team member — a gift that says you think of each person individually.": "Personalizovan komad za svakog člana tima — poklon koji kaže da mislite na svakog pojedinačno.",
    "New Year ornaments": "Novogodišnji ukrasi",
    "Handmade ornaments with your company's logo or message. A gift that returns to the tree every year.": "Ručno rađeni ukrasi sa logom ili porukom firme. Poklon koji se vraća na jelku svake godine.",
    "Wedding ornaments and favors": "Ukrasi i pokloni za vjenčanja",
    "Personalized ornaments with the newlyweds' names and date — a keepsake for guests that lasts longer than the cake.": "Personalizovani ukrasi sa imenima mladenaca i datumom — uspomena za goste koja traje duže od torte.",
    "Products from our collection": "Proizvodi iz naše ponude",
    "No time for custom production? Pick ready-made products from our collection — we can pack them as a gift set.": "Nemate vremena za custom izradu? Izaberite gotove proizvode iz naše kolekcije — možemo ih upakovati kao poklon set.",
    "Something else?": "Nešto drugo?",
    "Have an idea you don't see here? Describe it to us — if it's porcelain, it's probably doable.": "Imate ideju koju ovdje ne vidite? Opišite nam je — ako je od porcelana, vjerovatno je izvodljivo.",
    "Send us your idea": "Pošaljite nam ideju",
    "Not sure what you want? Perfect.": "Ne znate tačno šta želite? Idealno.",
    "Most companies reach out without a finished idea. Answer a few short questions — based on your answers we send a concrete proposal with prices, within 48h. No obligation whatsoever.": "Većina firmi nam se javi bez gotove ideje. Odgovorite na nekoliko kratkih pitanja — na osnovu vaših odgovora šaljemo konkretan prijedlog sa cijenama, u roku od 48h. Bez ikakve obaveze.",
    "Step {current} of {total}": "Korak {current} od {total}",
    "Next": "Dalje",
    "What's the occasion?": "Za koju priliku su pokloni?",
    "New Year / holidays": "Nova godina / praznici",
    "Company anniversary or celebration": "Godišnjica ili slavlje firme",
    "Gifts for employees or new colleagues": "Pokloni za zaposlene ili nove kolege",
    "Conference or event": "Konferencija ili event",
    "Wedding": "Vjenčanje",
    "Something else": "Nešto drugo",
    "When do you need the gifts by?": "Do kada bi vam pokloni trebali?",
    "Within the next month": "U narednih mjesec dana",
    "In 1-3 months": "Za 1–3 mjeseca",
    "In more than 3 months": "Za više od 3 mjeseca",
    "No exact date yet": "Nemam još tačan datum",
    "Handmade production takes about 4 weeks, so knowing your deadline helps us propose what's realistically doable.": "Ručna izrada traje oko 4 sedmice, pa nam rok pomaže da vam predložimo ono što je realno izvodljivo.",
    "What feels like the right gift?": "Šta vam se čini kao pravi poklon?",
    "(you can select multiple)": "(možete izabrati više opcija)",
    "Mugs": "Šoljice",
    "Plates": "Tanjiri",
    "Ornaments": "Ukrasi",
    "A combined set": "Kombinovani set",
    "Not sure — you suggest": "Nisam siguran/na — predložite vi",
    "What kind of personalization do you have in mind?": "Kakvu personalizaciju zamišljate?",
    "Company logo (3D or painted)": "Logo firme (3D ili oslikan)",
    "Newlyweds' names and date": "Imena mladenaca i datum",
    "Recipients' names": "Imena primalaca",
    "Your brand colors": "Boje vašeg brenda",
    "A message, date or slogan": "Poruka, datum ili slogan",
    "No personalization — ready-made products": "Bez personalizacije — gotovi proizvodi iz ponude",
    "Not sure — advise me": "Nisam siguran/na — posavjetujte me",
    "Roughly how many gifts do you need?": "Koliko poklona vam okvirno treba?",
    "Up to 10": "Do 10",
    "Over 100": "Preko 100",
    "Don't know yet": "Ne znam još",
    "Approximate budget per gift? (optional)": "Okvirni budžet po poklonu? (opciono)",
    "Up to 10 €": "Do 10 €",
    "Over 40 €": "Preko 40 €",
    "I'd rather hear your suggestion": "Radije bih vaš prijedlog",
    "A rough estimate is completely fine — the price per piece adjusts to quantity and personalization.": "Okvirno je sasvim dovoljno — cijena po komadu se prilagođava količini i vrsti personalizacije.",
    "Just your contact — and the offer is on its way": "Još samo kontakt — i ponuda kreće ka vama",
    "Company (optional)": "Firma (opciono)",
    "Phone (optional)": "Telefon (opciono)",
    "If you'd like us to call you": "Ako želite da vas pozovemo",
    "Tell us anything you think would help — ideas, colors, a link to your website...": "Recite nam sve što mislite da bi nam pomoglo — ideje, boje, link na vaš sajt...",
    "Send inquiry": "Pošaljite upit",
    "We reply within 48h with a concrete proposal and prices. We use your details only to answer your inquiry.": "Odgovaramo u roku od 48h sa konkretnim prijedlogom i cijenama. Vaše podatke koristimo isključivo da vam odgovorimo na upit.",
    "Thank you! Your inquiry is with us.": "Hvala! Vaš upit je kod nas.",
    "We'll review your answers and get back to you within 48h with a proposal and prices. If anything is unclear, we'll call you — so a phone number is worth gold.": "Pregledaćemo vaše odgovore i javiti vam se u roku od 48h sa prijedlogom i cijenama. Ako nam nešto bude nejasno, pozvaćemo vas — zato je broj telefona zlata vrijedan.",
    "In the meantime, take a peek into our workshop on Instagram:": "U međuvremenu, zavirite u našu radionicu na Instagramu:",
    "From our workshop": "Iz naše radionice",
    "Mugs with a 3D logo for Biznis Price": "Šoljice sa 3D logom za Biznis Price",
    "For the Biznis Price team we made mugs with the company's 3D logo — each one cast, sanded and painted by hand in our atelier. The logo isn't printed, it's modeled as part of the mug itself.": "Za tim Biznis Price izradili smo šoljice sa 3D logom firme — svaka je ručno izlivena, brušena i oslikana u našem ateljeu. Logo nije štampan, nego modelovan kao dio same šoljice.",
    "Personalized wedding ornaments": "Personalizovani ukrasi za vjenčanja",
    "We make ornaments with names and dates for newlyweds across the region — the same care and the same hands that make gifts for companies.": "Ukrase sa imenima i datumom radimo za mladence širom regiona — ista pažnja i iste ruke koje prave poklone za firme.",
    "Every piece that leaves our workshop has passed through our hands — from liquid porcelain to a packed gift.": "Svaki komad koji izađe iz naše radionice prošao je kroz naše ruke — od tečnog porcelana do spakovanog poklona.",
    "How the collaboration works": "Kako izgleda saradnja",
    "You send an inquiry": "Pošaljete upit",
    "Through the form above (60 seconds), by email or by phone — whatever suits you.": "Kroz formu iznad (60 sekundi), emailom ili telefonom — kako vam je zgodnije.",
    "You get a proposal and an offer": "Dobijete prijedlog i ponudu",
    "Within 48h we send a concrete proposal with photos of similar work and prices for your quantity.": "U roku od 48h šaljemo konkretan prijedlog sa fotografijama sličnih izrada i cijenama za vašu količinu.",
    "You confirm the design": "Potvrdite dizajn",
    "Before production we agree on the look — colors, logo, packaging. For larger batches a sample piece is possible.": "Prije izrade usaglašavamo izgled — boje, logo, pakovanje. Za veće serije moguć je i probni primjerak.",
    "Production and delivery": "Izrada i isporuka",
    "Handmade production takes about 4 weeks from design confirmation. We deliver across the region, ready to hand over.": "Ručna izrada traje oko 4 sedmice od potvrde dizajna. Isporučujemo širom regiona, spremno za uručenje.",
    "Frequently asked questions": "Česta pitanja",
    "What is the minimum quantity?": "Koja je minimalna količina?",
    "We don't have a strict minimum — we've done small batches too. For smaller quantities the price per piece is somewhat higher, and for larger batches it adjusts. Tell us roughly how many you need and we'll send an exact calculation.": "Nemamo strogu minimalnu količinu — radili smo i male serije. Za manje količine cijena po komadu je nešto viša, a za veće serije se prilagođava. Recite nam koliko vam okvirno treba i poslaćemo tačnu kalkulaciju.",
    "How long does production take?": "Koliko traje izrada?",
    "About 4 weeks from design confirmation, because every piece is made by hand. For New Year gifts we recommend contacting us by early November at the latest — the end of the year fills up fast.": "Oko 4 sedmice od potvrde dizajna, jer se svaki komad izrađuje ručno. Za novogodišnje poklone preporučujemo da nam se javite najkasnije početkom novembra — kraj godine se brzo popuni.",
    "How is the price determined?": "Kako se formira cijena?",
    "The price depends on the type of product, personalization and quantity. That's why we don't keep a price list on the site — instead, for every inquiry we send a concrete offer within 48h, with no obligation.": "Cijena zavisi od vrste proizvoda, personalizacije i količine. Zato ne držimo cjenovnik na sajtu — nego za svaki upit šaljemo konkretnu ponudu u roku od 48h, bez obaveze.",
    "What format should we send the logo in?": "U kom formatu treba da pošaljemo logo?",
    "Ideally in a vector format (AI, SVG, PDF), but send what you have — we'll manage. For a 3D logo our designer prepares a model that we send you for approval before production.": "Idealno u vektorskom formatu (AI, SVG, PDF), ali pošaljite šta imate — snaći ćemo se. Za 3D logo pripremamo model koji vam šaljemo na potvrdu prije izrade.",
    "Can we see what it will look like before the whole batch is made?": "Možemo li vidjeti kako će izgledati prije izrade cijele serije?",
    "Yes — before production you receive a visual preview, and for larger batches a sample piece can be arranged.": "Da — prije izrade dobijate vizuelni prikaz, a za veće serije moguće je dogovoriti i izradu probnog primjerka.",
    "Do you work with companies — invoice, billing?": "Da li radite sa firmama — račun, faktura?",
    "Yes. We issue an invoice to your company and we're used to procurement procedures — pro forma invoices, deadlines and everything your accounting department needs.": "Da. Izdajemo fakturu na firmu i navikli smo na proceduru nabavke — predračun, rokove i sve što vaše računovodstvo traži.",
    "Can you also wrap the gifts?": "Da li poklone možete i upakovati?",
    "Yes — every gift arrives in packaging ready to hand over. We can adapt the packaging to the occasion, and for companies add a personalized message.": "Da — svaki poklon stiže u pakovanju spremnom za uručenje. Pakovanje možemo prilagoditi prilici, a za firme dodati i personalizovanu poruku.",
    "Where do you deliver?": "Gdje sve isporučujete?",
    "Across the region — Bosnia and Herzegovina, Serbia, Croatia, Montenegro. For shipping outside the region — ask, and we'll work it out.": "Širom regiona — Bosna i Hercegovina, Srbija, Hrvatska, Crna Gora. Za slanje van regiona — pitajte, dogovorićemo se.",
    "Prefer to talk?": "Radije razgovarate?",
    "We understand — sometimes it's easier to tell than to click. Write to us or call, and together we'll figure out what would make your team (or your wedding guests) happiest.": "Razumijemo — nekad je lakše ispričati nego kliktati. Pišite nam ili nazovite, pa ćemo zajedno smisliti šta bi vaš tim (ili vaše svatove) najviše obradovalo.",
    "Or build your inquiry in 60 seconds": "Ili sastavite upit za 60 sekundi"
  },
  "RU": {
    "Shop By Categories": "Категории",
    "Available Now": "В наличии",
    "Ceramics": "Керамика",
    "Add to cart": "В корзину",
    "Added to cart": "В корзине",
    "Order Now": "Заказать",
    "Loading...": "Загрузка...",
    "Read more": "Подробнее",
    "Show less": "Свернуть",
    "Home": "Главная",
    "Contact": "Контакты",
    "About": "О нас",
    "Shop": "Магазин",
    "Paintings": "Картины",
    "Search products...": "Поиск товаров...",
    "Search": "Поиск",
    "Cart": "Корзина",
    "Your cart is empty.": "Корзина пуста.",
    "View Cart": "Открыть корзину",
    "My Cart": "Корзина",
    "Delivery": "Доставка",
    "Subtotal": "Подытог",
    "Total": "Всего",
    "Qty:": "Кол-во:",
    "Quantity": "Количество",
    "Cart is empty.": "Корзина пуста.",
    "Checkout": "Оформить заказ",
    "Edit Cart": "Изменить корзину",
    "Order": "Заказать",
    "Remove": "Удалить",
    "Continue shopping": "Продолжить покупки",
    "Your cart": "Ваша корзина",
    "Browse paintings": "Смотреть картины",
    "Free": "Бесплатно",
    "Continue to payment": "Перейти к оплате",
    "Cash on delivery · Delivery across the region": "Оплата при получении · Доставка по региону",
    "Set": "Набор",
    "Color": "Цвет",
    "Set is currently out of stock": "Набора сейчас нет в наличии",
    "Accessories": "Аксессуары",
    "Description": "Описание",
    "Date": "Дата",
    "Reference images": "Референсные изображения",
    "Delivery within": "Доставка в течение",
    "Due to handmade production and personalization, the mug takes about 4 weeks to make.": "Из-за ручного изготовления и персонализации кружка изготавливается около 4 недель.",
    "Due to handmade production and personalization, the plate takes about 4 weeks to make.": "Из-за ручного изготовления и персонализации тарелка изготавливается около 4 недель.",
    "Product is currently out of stock": "Товара сейчас нет в наличии",
    "Notify me when available": "Сообщить, когда появится",
    "Out of stock": "Нет в наличии",
    "In stock": "В наличии",
    "Available": "В наличии",
    "Currently unavailable": "Сейчас недоступна",
    "SALE": "СКИДКА",
    "Choose size": "Выбрать размер",
    "Order Summary": "Сводка заказа",
    "Order summary": "Сводка заказа",
    "Thank You": "Спасибо",
    "Your order has beed received": "Ваш заказ принят",
    "You will receive an email once your order is confirmed.": "Вы получите письмо, как только заказ будет подтверждён.",
    "Products": "Товары",
    "Information": "Информация",
    "Delivery & Returns": "Доставка и возврат",
    "Privacy Policy": "Политика конфиденциальности",
    "Terms of Use": "Условия использования",
    "Contact us": "Свяжитесь с нами",
    "All rights reserved.": "Все права защищены.",
    "View all": "Смотреть все",
    "Workshops": "Мастер-классы",
    "Ceramic angel": "Керамический ангел",
    "Shipping is calculated at checkout": "Доставка рассчитывается при оформлении",
    "This site uses cookies for better functionality.": "Этот сайт использует файлы cookie для улучшения функциональности.",
    "Learn more": "Узнать больше",
    "OK": "Хорошо",
    "Order details": "Детали заказа",
    "Order ID": "Номер заказа",
    "Payment method": "Способ оплаты",
    "Ordered products": "Заказанные товары",
    "Name": "Имя",
    "Email": "Эл. почта",
    "Phone": "Телефон",
    "Country": "Страна",
    "City": "Город",
    "Address": "Адрес",
    "Text": "Текст",
    "Bosnia and Herzegovina": "Босния и Герцеговина",
    "Serbia": "Сербия",
    "Croatia": "Хорватия",
    "Montenegro": "Черногория",
    "This field is required": "Это поле обязательно",
    "Invalid email address": "Неверный адрес эл. почты",
    "Invalid phone number": "Неверный номер телефона",
    "Customer information": "Данные покупателя",
    "Confirmation email": "Эл. почта для подтверждения заказа",
    "First name": "Имя",
    "Last name": "Фамилия",
    "Phone number": "Номер телефона",
    "Shipping information": "Данные для доставки",
    "Zip code": "Почтовый индекс",
    "Cash on delivery": "Оплата при получении",
    "Card payment": "Оплата картой",
    "European Union": "Европейский союз",
    "Shipping to this country isn't available yet. Please contact us on Instagram.": "Доставка в эту страну пока недоступна. Свяжитесь с нами в Instagram.",
    "Enter a valid Bosnian phone number": "Введите корректный номер телефона Боснии и Герцеговины",
    "Enter a valid Serbian phone number": "Введите корректный номер телефона Сербии",
    "Enter a valid Montenegrin phone number": "Введите корректный номер телефона Черногории",
    "Enter a valid Croatian phone number": "Введите корректный номер телефона Хорватии",
    "Shipping method": "Способ доставки",
    "Payment": "Оплата",
    "Show order summary": "Показать сводку заказа",
    "Hide order summary": "Скрыть сводку заказа",
    "Pay in cash when the courier delivers your order.": "Оплата наличными курьеру при получении заказа.",
    "You will enter your card details securely on the next step.": "Данные карты вы безопасно введёте на следующем шаге.",
    "All transactions are secure and encrypted.": "Все платежи защищены и зашифрованы.",
    "Name on card": "Имя на карте",
    "Use shipping address as billing address": "Использовать адрес доставки как платёжный адрес",
    "Courier delivery to your address": "Доставка курьером по вашему адресу",
    "Pay": "Оплатить",
    "Amount due": "Сумма к оплате",
    "Checking payment...": "Проверяем оплату...",
    "Payment failed. Please try again.": "Оплата не прошла. Попробуйте ещё раз.",
    "Payment is being processed. You will receive a confirmation email once it is completed.": "Платёж обрабатывается. Вы получите письмо с подтверждением, как только он завершится.",
    "Card payment is unavailable right now. Please try again later or contact us on Instagram.": "Оплата картой сейчас недоступна. Попробуйте позже или свяжитесь с нами в Instagram.",
    "This payment was canceled. Please contact us on Instagram.": "Этот платёж отменён. Свяжитесь с нами в Instagram.",
    "Your order is saved and will be confirmed once the payment is completed.": "Ваш заказ сохранён и будет подтверждён после оплаты.",
    "Processing...": "Обработка...",
    "Limited stock": "Ограниченный запас",
    "Additional information": "Дополнительная информация",
    "Gold text inside the mug (optional)": "Золотой текст внутри чашки (опционально)",
    "Describe the details you want on the platter:": "Опишите детали, которые хотите на подносе:",
    "e.g. I love you ❤️": "Напр. Люблю тебя ❤️",
    "Example: We have a dog, she loves walks, could be a cake and balloons, or a cup of tea": "Например: у нас есть собака, она любит прогулки, может быть торт и шарики или чашка чая",
    "Date you need the product": "Дата, когда вам нужен товар",
    "Choose a date": "Выберите дату",
    "Earliest available date": "Самая ранняя доступная дата",
    "The product requires at least 1 month to make. Please choose a date at least 1 month from today": "Изготовление товара занимает не менее 1 месяца. Пожалуйста, выберите дату не раньше, чем через 1 месяц",
    "or later": "или позже",
    "Choose a color": "Выберите цвет",
    "Choose a bead color": "Выберите цвет бусины",
    "You can choose more than one color.": "Можно выбрать несколько цветов.",
    "Selected color": "Выбранный цвет",
    "Your first accessory is free.": "Первый аксессуар бесплатный.",
    "Green": "Зелёная",
    "Pink": "Розовая",
    "Blue": "Синяя",
    "White/beige": "Белая/бежевая",
    "Black": "Чёрная",
    "Add accessories (optional)": "Добавить аксессуары (опционально)",
    "Want something else? Describe your own accessory": "Хотите что-то другое? Опишите свой аксессуар",
    "each": "за штуку",
    "e.g. a small crown, a bow tie, a fish…": "напр. маленькая корона, бабочка, рыбка…",
    "Add your own accessory": "Добавить свой аксессуар",
    "custom accessories": "своих аксессуаров",
    "Custom accessories": "Свои аксессуары",
    "How many pets on the mug?": "Сколько питомцев на кружке?",
    "How many pets on the pendant?": "Сколько питомцев на подвеске?",
    "How many pets on the plate?": "Сколько питомцев на тарелке?",
    "How many pets on the jewelry dish?": "Сколько питомцев на блюдце для украшений?",
    "Each additional pet": "Каждый следующий питомец",
    "Reference images of all pets": "Фото всех питомцев",
    "1 pet": "1 питомец",
    "2 pets": "2 питомца",
    "Included in price": "Включено в цену",
    "Number of pets": "Количество питомцев",
    "Add a reference photo of both pets so we can paint them together.": "Добавьте фото обоих питомцев, чтобы мы нарисовали их вместе.",
    "for": "за",
    "accessory": "аксессуар",
    "accessories (count)": "аксессуара",
    "optional": "опционально",
    "The following files are not allowed (only images are allowed):": "Следующие файлы не разрешены (разрешены только изображения):",
    "Error processing images. Please try again.": "Ошибка при обработке изображений. Пожалуйста, попробуйте снова.",
    "Error processing images.": "Ошибка при обработке изображений.",
    "You must add at least 1 image.": "Обязательно добавьте хотя бы 1 изображение.",
    "Add at least one clear photo of each pet.": "Добавьте хотя бы одно чёткое фото каждого питомца.",
    "Reference images of both pets": "Референсные фото обоих питомцев",
    "You can add up to": "Можно добавить до",
    "images": "изображений",
    "image": "изображение",
    "Allowed formats: JPEG, PNG, GIF, WEBP.": "Разрешённые форматы: JPEG, PNG, GIF, WEBP.",
    "You've added the maximum number of images (3/3).": "Вы добавили максимальное количество изображений (3/3).",
    "You've added the maximum number of images.": "Вы добавили максимальное количество изображений.",
    "Choose a set": "Выберите набор",
    "Sold out": "Распродано",
    "Low stock": "Мало на складе",
    "items left": "осталось шт.",
    "Please fill in all required fields": "Пожалуйста, заполните все обязательные поля",
    "Please enter text": "Пожалуйста, введите текст",
    "Please add an image": "Пожалуйста, добавьте изображение",
    "Added ✓": "Добавлено ✓",
    "Product information": "Информация о продукте",
    "Delivery information": "Информация о доставке",
    "Over 100+ happy pets already have their pendant.": "Более 100+ довольных питомцев уже получили свою подвеску ❤️",
    "Over 100+ happy pets already have their mug.": "Более 100+ довольных питомцев уже получили свою кружку ❤️",
    "Bulka Ceramics customer pet": "Питомец нашего клиента на подвеске Bulka Ceramics",
    "Packaging": "Упаковка",
    "The box our little plates and mugs arrive in.": "Коробка, в которой прибывают наши тарелочки и чашки.",
    "customer comment": "отзыв покупателя",
    "You may also like": "Вам может понравиться",
    "has arrived!": "уже здесь!",
    "is coming soon!": "скоро появится!",
    "View the summer collection": "Посмотреть летнюю коллекцию",
    "View the entire summer collection": "Посмотреть всю летнюю коллекцию",
    "Coming soon": "Скоро",
    "New collection coming soon!": "Новая коллекция скоро появится!",
    "Days": "Дней",
    "Hours": "Часов",
    "Minutes": "Минут",
    "Seconds": "Секунд",
    "View the entire collection": "Посмотреть всю коллекцию",
    "View all partnership products": "Смотреть все партнёрские товары",
    "Ceramics workshops": "Мастер-классы по керамике",
    "Reserve a spot": "Забронировать место",
    "2-3 hours": "2-3 часа",
    "8-12 participants": "8-12 участников",
    "Our Story": "Наша история",
    "Who is Bulka?": "Кто такая Булка?",
    "Select country": "Выберите страну",
    "Category not found": "Категория не найдена",
    "Category \"{name}\" does not exist.": "Категория \"{name}\" не существует.",
    "Back to home": "Вернуться на главную",
    "Showing {count} products": "Показано товаров: {count}",
    "Sort by:": "Сортировать по:",
    "Featured": "Рекомендуемые",
    "Price, low to high": "Цена: по возрастанию",
    "Price, high to low": "Цена: по убыванию",
    "Date, oldest to newest": "Дата: сначала старые",
    "Date, newest to oldest": "Дата: сначала новые",
    "No products in this category": "В этой категории нет товаров",
    "No products currently available to display.": "Сейчас нет доступных товаров для показа.",
    "Collection not found": "Коллекция не найдена",
    "Collection \"{name}\" does not exist.": "Коллекция \"{name}\" не существует.",
    "Loading collection...": "Загрузка коллекции...",
    "No products in this collection": "В этой коллекции нет товаров",
    "Limited edition. Available until {date} or while supplies last.": "Ограниченная серия. Доступно до {date} или до последнего экземпляра.",
    "No products": "Нет товаров",
    "Exclusive collection of ceramic products created through the Bulkaceramics and AteljeStojanović collaboration.": "Эксклюзивная коллекция керамических изделий, созданная в результате сотрудничества Bulkaceramics и AteljeStojanović.",
    "Each piece is handmade with special attention to detail.": "Каждое изделие изготовлено вручную с особым вниманием к деталям.",
    "Join our interactive ceramics workshops and discover the magic of creating with your own hands. You will learn the basic techniques of working with clay, shaping, and drawing. The workshops are suitable for all ages and experience levels — from beginners to those who want to perfect their skills.": "Присоединяйтесь к нашим интерактивным мастер-классам по керамике и откройте для себя магию создания своими руками. Вы освоите основные техники работы с глиной, лепку и роспись. Мастер-классы подходят для всех возрастов и уровней подготовки — от новичков до тех, кто хочет усовершенствовать свои навыки.",
    "Each participant will have the opportunity to create their own unique ceramic piece to take home as a keepsake after firing.": "Каждый участник сможет создать своё уникальное керамическое изделие, которое заберёт домой на память после обжига.",
    "This is the kind of box you treasure just as much as what's inside. 😍 Pure magic! ✨ I would never have the heart to throw it away. 🙈🥰": "Это та коробка, которую бережёшь так же, как и то, что внутри. 😍 Чистая магия! ✨ У меня никогда не поднялась бы рука её выбросить. 🙈🥰",
    "Bulka Ceramics is a family brand born from a love of creating unique ceramics. After years of perfecting our craft, we overcame challenges in the firing process and created collections that tell a story.": "Bulka Ceramics — это семейный бренд, рождённый из любви к созданию уникальной керамики. После многих лет совершенствования ремесла мы преодолели трудности в процессе обжига и создали коллекции, которые рассказывают историю.",
    "Bulka is the nickname of our beloved bull terrier Bea, who is the heart and soul of our brand. Her energy, persistence, and unique character inspire us in creating every ceramic piece.": "Bulka — это прозвище нашего любимого бультерьера Беи, которая является сердцем и душой нашего бренда. Её энергия, упорство и уникальный характер вдохновляют нас на создание каждого керамического изделия.",
    "New Year Collection": "Новогодняя коллекция",
    "New Year collection is coming soon": "Новогодняя коллекция скоро появится",
    "Handmade ceramic ornaments and gifts for the holidays, made in small batches.": "Керамические украшения и подарки ручной работы к праздникам, небольшими партиями.",
    "Leave your email and we'll let you know as soon as it's out.": "Оставьте email, и мы сообщим вам, как только она выйдет.",
    "Your email": "Ваш email",
    "Thank you! We'll let you know as soon as the collection is out.": "Спасибо! Мы сообщим вам, как только коллекция выйдет.",
    "Something went wrong. Please try again.": "Что-то пошло не так. Попробуйте ещё раз.",
    "No products in the New Year collection": "В новогодней коллекции нет товаров",
    "Invalid email format": "Неверный формат email адреса",
    "Your submission has been received. We will be in touch and contact you soon!": "Ваша заявка получена. Мы свяжемся с вами в ближайшее время!",
    "Error!": "Ошибка!",
    "We couldn't complete your request. Please contact us via email at contact@bulkaceramics.com": "Мы не смогли выполнить ваш запрос. Пожалуйста, свяжитесь с нами по электронной почте contact@bulkaceramics.com",
    "First Name": "Имя",
    "Last Name": "Фамилия",
    "Email*": "Email*",
    "Subject": "Тема",
    "Message": "Сообщение",
    "Submit": "Отправить",
    "Shipping": "Доставка",
    "For Serbia:": "Для Сербии:",
    "For Bosnia and Herzegovina:": "Для Боснии и Герцеговины:",
    "For Montenegro:": "Для Черногории:",
    "For Croatia:": "Для Хорватии:",
    "Delivery within 30 days.": "Доставка осуществляется в течение 30 дней.",
    "Delivery within 20 days.": "Доставка осуществляется в течение 20 дней.",
    "By arrangement.": "Доставка по договорённости.",
    "If your product is damaged during delivery, please send us photos of the damaged item within 3 business days. In that case, we can send a new product or issue a refund.": "Если товар был повреждён при доставке, пожалуйста, пришлите нам фотографии повреждённого изделия в течение 3 рабочих дней. В этом случае мы можем отправить новый товар или оформить возврат средств.",
    "If we have the product available, we can replace it immediately. If the product is not available, we will issue a refund.": "Если товар есть в наличии, мы можем сразу же его заменить. Если товара нет в наличии, мы оформим возврат средств.",
    "If you would like to return a product, please contact us at:": "Если вы хотите вернуть товар, пожалуйста, свяжитесь с нами по адресу:",
    "Our team will help you with all the necessary information and the return process.": "Наша команда поможет вам со всей необходимой информацией и процессом возврата.",
    "For international delivery, feel free to contact us at the same email.": "По вопросам международной доставки свяжитесь с нами по тому же адресу электронной почты.",
    "Reservation successfully sent!": "Бронирование успешно отправлено!",
    "Workshop:": "Мастер-класс:",
    "Time:": "Время:",
    "Error: {message}": "Ошибка: {message}",
    "An error occurred while sending the reservation. Please try again.": "Произошла ошибка при отправке бронирования. Пожалуйста, попробуйте снова.",
    "An error occurred while signing up for notifications. Please try again.": "Произошла ошибка при подписке на уведомления. Пожалуйста, попробуйте снова.",
    "Ceramic Workshops": "Мастер-классы по керамике",
    "Discover the magic of creating with your own hands. Our workshops are suited to all ages and experience levels - from beginners to those looking to refine their skills.": "Откройте для себя магию создания вещей своими руками. Наши мастер-классы подходят для всех возрастов и уровней подготовки - от новичков до тех, кто хочет усовершенствовать свои навыки.",
    "Gallery from our workshops": "Галерея с наших мастер-классов",
    "Locations and schedule": "Локации и расписание",
    "Loading workshops...": "Загрузка мастер-классов...",
    "No workshops currently available": "Сейчас нет доступных мастер-классов",
    "Fully booked": "Мест нет",
    "Open": "Доступно",
    "per person": "с человека",
    "Available times:": "Доступное время:",
    "1 spot available": "1 место доступно",
    "{n} spots available": "{n} мест доступно",
    "Reserve": "Забронировать",
    "What can you expect?": "Что вас ждёт?",
    "Learning basic techniques": "Изучение базовых техник",
    "Shaping, glazing and basic clay working techniques": "Формовка, глазирование и базовые техники работы с глиной",
    "Creating your own piece": "Создание собственного изделия",
    "Each participant creates their own unique ceramic piece": "Каждый участник создаёт своё уникальное керамическое изделие",
    "Limited number of spots": "Ограниченное количество мест",
    "The number of spots is limited to ensure dedicated attention to each participant": "Количество мест ограничено, чтобы уделить внимание каждому участнику",
    "Location: {name}": "Место: {name}",
    "Email address": "Электронная почта",
    "Full name": "Полное имя",
    "Your name": "Ваше имя",
    "Cancel": "Отмена",
    "Sign me up": "Записать меня",
    "Workshop reservation": "Бронирование мастер-класса",
    "First name *": "Имя *",
    "Last name *": "Фамилия *",
    "Your last name": "Ваша фамилия",
    "Email address *": "Адрес электронной почты *",
    "Phone number *": "Номер телефона *",
    "Note (optional)": "Примечание (необязательно)",
    "Special requests or questions...": "Особые пожелания или вопросы...",
    "Confirm reservation": "Подтвердить бронирование",
    "Notification": "Уведомление",
    "Email is required": "Email обязателен",
    "Please add at least one image": "Добавьте хотя бы одно изображение",
    "Thank you!": "Спасибо!",
    "Personalized Illustrations": "Персонализированные иллюстрации",
    "Custom illustrations": "Персонализированные иллюстрации",
    "Turn your favorite photo into a unique illustration made to your wishes. Illustrations are printed as high-quality giclée prints in formats from A5 to A2.": "Превратите свою любимую фотографию в уникальную иллюстрацию, созданную по вашим пожеланиям. Иллюстрации печатаются в виде высококачественных жикле-принтов форматов от A5 до A2.",
    "Price list:": "Прайс-лист:",
    "For one person:": "За одного человека:",
    "Up to A4:": "До A4:",
    "For two or three people:": "За двух или трёх человек:",
    "Up to A3:": "До A3:",
    "Additional people:": "Дополнительные люди:",
    "Pets:": "Питомцы:",
    "Included at no extra charge": "Включены без дополнительной платы",
    "Treat yourself or a loved one to a memory that will brighten up any space!": "Подарите себе или близкому человеку воспоминание, которое украсит любое пространство!",
    "How to send photos for illustration?": "Как отправить фотографии для иллюстрации?",
    "Attach clear photos": "Прикрепите чёткие фотографии",
    "that you want used to create the illustration.": "которые хотите использовать для создания иллюстрации.",
    "We recommend high-quality photos, with good lighting and visible facial and clothing details.": "Рекомендуем фотографии высокого качества, с хорошим освещением и видимыми деталями лица и одежды.",
    "You can attach multiple photos if you want to combine different details in one illustration.": "Вы можете прикрепить несколько фотографий, если хотите объединить разные детали в одной иллюстрации.",
    "If you have special requests (backgrounds, colors, clothing style or additional elements), write them in the description.": "Если у вас есть особые пожелания (фон, цвета, стиль одежды или дополнительные элементы), укажите их в описании.",
    "Allowed photo formats:": "Разрешённые форматы фотографий:",
    "Click on": "Нажмите на",
    "Add images": "Добавить изображения",
    "to attach your photos, then fill in the other details and submit your request.": "чтобы прикрепить свои фотографии, затем заполните остальные данные и отправьте запрос.",
    "Field is required": "Поле обязательно для заполнения",
    "Proposal received": "Предложение получено",
    "Thank you for your interest. We carefully consider every proposal and will get back to you if we believe there's a good fit.": "Спасибо за проявленный интерес. Мы внимательно рассматриваем каждое предложение и свяжемся с вами, если сочтём, что есть хорошее соответствие.",
    "Error": "Ошибка",
    "We couldn't receive your proposal. Please contact us directly at contact@bulkaceramics.com": "Мы не смогли получить ваше предложение. Пожалуйста, свяжитесь с нами напрямую по адресу contact@bulkaceramics.com",
    "Find us": "Найдите нас",
    "Store map": "Карта магазинов",
    "Our products are available at selected locations.": "Наши товары доступны в отдельных точках продаж.",
    "Shown on the map": "Показано на карте",
    "More locations coming soon": "Больше локаций скоро",
    "Partnership": "Партнёрство",
    "Your store": "Ваш магазин",
    "on this map?": "на этой карте?",
    "Our products can currently be found at selected locations. If you run a store and think our products would be a good fit — we'd love to hear from you. We choose partners carefully.": "Наши товары в настоящее время можно найти в отдельных точках продаж. Если вы управляете магазином и считаете, что наши товары хорошо впишутся — будем рады услышать от вас. Мы тщательно выбираем партнёров.",
    "Selective approach": "Избирательный подход",
    "Each partner goes through an individual selection process.": "Каждый партнёр проходит индивидуальный отбор.",
    "Handmade, high value": "Ручная работа, высокая ценность",
    "Products that sell themselves.": "Товары, которые продают себя сами.",
    "Growing brand": "Растущий бренд",
    "An active online community that directs customers to stores.": "Активное онлайн-сообщество, которое направляет покупателей в магазины.",
    "Store name": "Название магазина",
    "e.g. My Store": "напр. Мой Магазин",
    "Contact email": "Контактный email",
    "Why do you think we'd be a good fit?": "Почему вы думаете, что мы хорошо подходим друг другу?",
    "Tell us about your store, customers, and why you think our products would be a good choice...": "Расскажите нам о своём магазине, клиентах и почему вы считаете, что наши товары были бы хорошим выбором...",
    "Sending…": "Отправка…",
    "Send us your proposal": "Отправьте нам предложение",
    "Data Collection": "Сбор данных",
    "Data Usage": "Использование данных",
    "Data Protection": "Защита данных",
    "Cookies and Tracking Technologies": "Файлы cookie и технологии отслеживания",
    "Changes to the Privacy Policy": "Изменения политики конфиденциальности",
    "General Terms": "Общие условия",
    "Orders": "Заказы",
    "Limitation of Liability": "Ограничение ответственности",
    "Your privacy is very important to us. This privacy policy explains how we collect, use, and protect your personal data.": "Ваша конфиденциальность очень важна для нас. Эта политика конфиденциальности объясняет, как мы собираем, используем и защищаем ваши персональные данные.",
    "We collect information you voluntarily provide when registering on our website or when ordering products. This information includes, but is not limited to: name, address, email address, and phone number.": "Мы собираем информацию, которую вы добровольно предоставляете при регистрации на нашем сайте или при заказе товаров. Эта информация включает, но не ограничивается: имя, адрес, адрес электронной почты и номер телефона.",
    "We use your data to provide you with quality service, process your orders, and provide you with information about our products and services.": "Мы используем ваши данные, чтобы предоставлять вам качественный сервис, обрабатывать ваши заказы и предоставлять вам информацию о наших товарах и услугах.",
    "We take appropriate technical and organizational measures to protect your personal data from unauthorized access, loss, or destruction.": "Мы принимаем соответствующие технические и организационные меры для защиты ваших персональных данных от несанкционированного доступа, потери или уничтожения.",
    "Our website uses cookies and similar tracking technologies to improve your user experience.": "Наш сайт использует файлы cookie и аналогичные технологии отслеживания для улучшения вашего пользовательского опыта.",
    "You can set your cookie preferences in your browser settings. Please note that disabling cookies may affect your ability to use certain features of our website.": "Вы можете настроить параметры файлов cookie в настройках вашего браузера. Обратите внимание, что отключение файлов cookie может повлиять на вашу возможность использования некоторых функций нашего сайта.",
    "We reserve the right to change this privacy policy at any time. All changes will be published on this page, and the date of the last modification will be updated. We recommend that you check this page periodically to stay informed of any changes.": "Мы оставляем за собой право изменять эту политику конфиденциальности в любое время. Все изменения будут опубликованы на этой странице, а дата последнего изменения будет обновлена. Рекомендуем периодически проверять эту страницу, чтобы быть в курсе всех изменений.",
    "At any time, you can request a change to your personal data. For any inquiries, contact us at email:": "В любое время вы можете запросить изменение ваших персональных данных. По любым вопросам свяжитесь с нами по электронной почте:",
    "By using this site, you agree to the following terms of use:": "Используя этот сайт, вы соглашаетесь со следующими условиями использования:",
    "All content on this site, including text, images, and graphics, is protected by copyright. Use of any part of the content without our permission is not allowed.": "Весь контент на этом сайте, включая текст, изображения и графику, защищён авторским правом. Использование любой части контента без нашего разрешения не допускается.",
    "When ordering products, the user undertakes to provide accurate and complete information. If an error occurs in the order, we reserve the right to cancel it.": "При заказе товаров пользователь обязуется предоставить точную и полную информацию. В случае ошибки в заказе мы оставляем за собой право отменить его.",
    "We are not responsible for any damages or losses that may result from the use of our products or services. We recommend that you carefully read all information before making a purchase.": "Мы не несём ответственности за любой ущерб или убытки, которые могут возникнуть в результате использования наших товаров или услуг. Рекомендуем внимательно ознакомиться со всей информацией перед совершением покупки.",
    "For any additional information or questions regarding our terms of use, feel free to contact us at email:": "По любой дополнительной информации или вопросам, касающимся наших условий использования, свяжитесь с нами по электронной почте:",
    "Page Not Found": "Страница не найдена",
    "Go to home": "На главную",
    "Follow on Instagram": "Подписывайтесь на Instagram",
    "Be the first to know": "Узнайте первыми",
    "when a new painting is": "когда новая картина",
    "available.": "доступна.",
    "Subscribed": "Вы подписаны",
    "Sending...": "Отправка...",
    "Subscribe me": "Подписать меня",
    "Your data is safe. We will never share it.": "Ваши данные в безопасности. Мы никогда не поделимся ими.",
    "We'll be in touch as soon as a new painting is available.": "Мы свяжемся с вами, как только появится новая картина.",
    "Close": "Закрыть",
    "What clients say": "Что говорят клиенты",
    "Paintings that": "Картины, которые",
    "leave a mark.": "оставляют след.",
    "Instagram message": "Сообщение в Instagram",
    "Available motifs": "Доступные мотивы",
    "Choose your": "Выберите свою",
    "painting": "картину",
    "Available paintings can be ordered right away. Paintings that are currently unavailable can be ordered via Instagram or email.": "Доступные картины можно заказать сразу. Картины, которые сейчас недоступны, можно заказать через Instagram или по электронной почте.",
    "Paintings that will fill": "Картины, которые наполнят",
    "your home with": "ваш дом",
    "love.": "любовью.",
    "View available paintings": "Смотреть доступные картины",
    "Each painting is created with special": "Каждая картина создаётся с особой",
    "care and love,": "заботой и любовью,",
    "created to stay in your home forever.": "созданная, чтобы остаться в вашем доме навсегда.",
    "Paintings that will fill your home with love.": "Картины, которые наполнят ваш дом любовью.",
    "Required field": "Обязательное поле",
    "Invalid email": "Неверный email",
    "Back": "Назад",
    "Customer details": "Данные покупателя",
    "Shipping address": "Адрес доставки",
    "ZIP code": "Почтовый индекс",
    "An error occurred while submitting your order. Please try again or contact us on Instagram.": "Произошла ошибка при отправке заказа. Попробуйте снова или свяжитесь с нами в Instagram.",
    "Estimated delivery": "Ориентировочный срок доставки",
    "Due to handmade craftsmanship and personalization, the product requires about 4 weeks to make.": "Из-за ручной работы и персонализации изделию требуется около 4 недель на изготовление.",
    "Thank you for your order!": "Спасибо за ваш заказ!",
    "Dear {name}, your order has been received.": "Уважаемый(ая) {name}, ваш заказ принят.",
    "Your order has been received.": "Ваш заказ принят.",
    "We'll be in touch as soon as the painting has been shipped.": "Мы свяжемся с вами, как только картина будет отправлена.",
    "Confirmation sent to": "Подтверждение отправлено на",
    "Order number": "Номер заказа",
    "Ordered paintings": "Заказанные картины",
    "Painting": "Картина",
    "Painting not found.": "Картина не найдена.",
    "Notify me": "Уведомить меня",
    "When it becomes available": "Когда появится в наличии",
    "Enter your email address and we will notify you when the painting becomes available again.": "Введите адрес электронной почты, и мы уведомим вас, когда картина снова станет доступна.",
    "You will be notified when the painting becomes available.": "Вы получите уведомление, когда картина станет доступна.",
    "Send": "Отправить",
    "Enter your email address to be notified when the product becomes available again.": "Введите адрес электронной почты, чтобы получить уведомление, когда товар снова появится в наличии.",
    "You will be notified when the product becomes available.": "Вы получите уведомление, когда товар появится в наличии.",
    "Enter your email address": "Введите адрес электронной почты",
    "An error occurred while sending your data. Please try again.": "Произошла ошибка при отправке данных. Пожалуйста, попробуйте снова.",
    "Your cart has reached its maximum size. Please try again with smaller images.": "Корзина достигла максимального размера. Пожалуйста, попробуйте с изображениями меньшего размера.",
    "Please enter a valid email address": "Пожалуйста, введите корректный email адрес",
    "Error. Please try again.": "Ошибка. Пожалуйста, попробуйте снова.",
    "Delivery date": "Доставка",
    "*The painting comes on a wooden blind frame. A decorative frame can be added later.": "*Картина поставляется на деревянном подрамнике. Декоративную раму можно добавить позже.",
    "Personalization": "Персонализация",
    "Gold text inside the cup (optional)": "Золотой текст внутри чашки (опционально)",
    "Description of details you want on the tray": "Описание деталей, которые вы хотите видеть на подносе",
    "E.g. I love you ❤️": "Напр. Я люблю тебя ❤️",
    "For example: We have a dog, she loves walks, it can be a cake and balloons...": "Например: у нас есть собака, она любит прогулки, это может быть торт и шарики...",
    "Date you need the product by": "Дата, к которой вам нужен продукт",
    "Earliest date": "Самая ранняя дата",
    "The product requires at least 1 month to make ({date} or later).": "Изготовление продукта занимает не менее 1 месяца ({date} или позже).",
    "Allowed formats: JPEG, PNG, GIF, WEBP": "Разрешённые форматы: JPEG, PNG, GIF, WEBP",
    "You have added the maximum number of images (3/3).": "Вы добавили максимальное количество изображений (3/3).",
    "You must add at least 1 image. ": "Обязательно добавьте хотя бы 1 изображение. ",
    "You can add up to 3 images.": "Вы можете добавить до 3 изображений.",
    "Options": "Опции",
    "day": "день",
    "days": "дней",
    "Delivery time": "Доставка",
    "added to production time": "к сроку изготовления",
    "⚠ Limited stock — only {n} left": "⚠ Ограниченный запас — осталось только {n}",
    "Not available": "Недоступно",
    "Estimated delivery time is 1–2 months from order.": "Ориентировочное время доставки составляет 1–2 месяца с момента заказа.",
    "The delivery cost will be included at checkout.": "Стоимость доставки будет включена при оформлении заказа.",
    "Each product is handmade and carries its own unique character.": "Каждое изделие изготовлено вручную и обладает своим уникальным характером.",
    "All materials are lead-free and food safe.": "Все материалы не содержат свинца и безопасны для контакта с пищей.",
    "Each product is handmade and carries its own unique character. Due to handmade production, minor variations in shape, size, color, and pattern are possible, which makes each piece special and one of a kind.": "Каждое изделие изготовлено вручную и обладает своим уникальным характером. Из-за ручного изготовления возможны небольшие отклонения в форме, размере, цвете и узоре, что делает каждое изделие особенным и неповторимым.",
    "Made of porcelain, painted with glaze colors and covered with a transparent glaze with 24K gold details.": "Изготовлено из фарфора, расписано глазурными красками и покрыто прозрачной глазурью с деталями из 24-каратного золота.",
    "All materials used are lead-free, non-toxic, and completely safe for use with food.": "Все использованные материалы не содержат свинца, нетоксичны и полностью безопасны для контакта с пищей.",
    "To preserve the gold details and lasting shine, gentle hand washing is recommended.": "Для сохранения золотых деталей и долговременного блеска рекомендуется бережная ручная мойка.",
    "The estimated delivery date is shown at checkout.": "Ориентировочная дата доставки указана при оформлении заказа.",
    "Delivery is free for orders over 150 KM / 75 EUR / 9000 RSD.": "Доставка бесплатна для заказов свыше 150 KM / 75 EUR / 9000 RSD.",
    "Free shipping on all orders over 9000 RSD within Serbia": "Бесплатная доставка по Сербии при заказе от 9000 RSD",
    "Free shipping on all orders over 150 KM within Bosnia and Herzegovina": "Бесплатная доставка по Боснии и Герцеговине при заказе от 150 KM",
    "Free shipping on all orders over 80 EUR within Croatia": "Бесплатная доставка по Хорватии при заказе от 80 EUR",
    "Free shipping on all orders over 80 EUR within Montenegro": "Бесплатная доставка по Черногории при заказе от 80 EUR",
    "We recommend": "Мы рекомендуем",
    "You might also like": "Вам может понравиться",
    "Up to {n} images.": "До {n} изображений.",
    "January": "Январь",
    "February": "Февраль",
    "March": "Март",
    "April": "Апрель",
    "May": "Май",
    "June": "Июнь",
    "July": "Июль",
    "August": "Август",
    "September": "Сентябрь",
    "October": "Октябрь",
    "November": "Ноябрь",
    "December": "Декабрь",
    "Sun": "Вс",
    "Mon": "Пн",
    "Tue": "Вт",
    "Wed": "Ср",
    "Thu": "Чт",
    "Fri": "Пт",
    "Sat": "Сб"
  }
};

// Build a translate(key) function bound to a language.
// Falls back to the English key when a translation is missing, so a missing
// entry shows readable English rather than `undefined`.
// Pick a localized CONTENT field off an API object.
//   localized(product, 'name', 'RU')  -> product.nameRU || product.nameBA
// Falls back to the local (BA) value, then empty string, so a missing RU/US
// translation never renders blank.
export function localized(obj, base, language) {
  if (!obj) return '';
  return obj[base + language] || obj[base + 'BA'] || '';
}

// Pick a localized CATEGORY field. Category columns use a different convention
// than products: local text is the bare column (e.g. `title`), with `_us` /
// `_ru` suffixes for English / Russian.
//   localizedCat(category, 'title', 'RU') -> category.title_ru || category.title
export function localizedCat(obj, base, language) {
  if (!obj) return '';
  if (language === 'US') return obj[base + '_us'] || obj[base] || '';
  if (language === 'RU') return obj[base + '_ru'] || obj[base] || '';
  return obj[base] || '';
}

export function makeTranslate(language) {
  return (key) => {
    if (language === 'US') return key;
    const dict = translations[language];
    if (dict && key in dict) return dict[key];
    return key;
  };
}

// ─── Context ─────────────────────────────────────────────────────────────
// Lets any descendant component translate without prop drilling.
// `unlocked` is false for the general public (see i18n/languageAccess.js) —
// components use it to decide whether to render the language picker at all.
const LanguageContext = createContext({
  language: 'BA',
  setLanguage: () => {},
  translate: (k) => k,
  t: (k) => k,
  unlocked: false,
});

export function LanguageProvider({ language, setLanguage, unlocked = false, children }) {
  const translate = useMemo(() => makeTranslate(language), [language]);
  const value = useMemo(
    () => ({ language, setLanguage, translate, t: translate, unlocked }),
    [language, setLanguage, translate, unlocked]
  );
  return React.createElement(LanguageContext.Provider, { value }, children);
}

export function useLanguage() {
  return useContext(LanguageContext);
}
